import { collection, doc, getDocs, onSnapshot, setDoc, deleteDoc, type QueryDocumentSnapshot } from 'firebase/firestore'
import type { Station } from '../../types'
import { dispatchCatalogEvent, MENU_UPDATED_EVENT } from '../catalogEvents'
import { getFirestoreInstance } from '../firebase'
import { getCurrentPlaceId } from '../placeConfig'
import { stripUndefined } from './stripUndefined'
import { commitWriteOps, type BatchWriteOp } from './writeBatches'

const stationsCacheByPlace: Record<string, Station[]> = {}
let stationsUnsubscribe: (() => void) | null = null

function stationsRef(placeId: string) {
  return collection(getFirestoreInstance(), 'places', placeId, 'stations')
}

function stationDoc(placeId: string, stationId: string) {
  return doc(getFirestoreInstance(), 'places', placeId, 'stations', stationId)
}

function docToStation(d: QueryDocumentSnapshot): Station {
  const data = d.data() as Record<string, unknown>
  return {
    id: d.id,
    name: (data.name as string) ?? '',
    menuKey: typeof data.menuKey === 'string' ? data.menuKey : undefined,
  }
}

function dispatchMenuUpdated(): void {
  dispatchCatalogEvent(MENU_UPDATED_EVENT)
}

export async function primeStationsCache(): Promise<void> {
  const placeId = getCurrentPlaceId()
  if (stationsUnsubscribe) {
    stationsUnsubscribe()
    stationsUnsubscribe = null
  }
  const snap = await getDocs(stationsRef(placeId))
  stationsCacheByPlace[placeId] = snap.docs.map((d) => docToStation(d))
  stationsUnsubscribe = onSnapshot(stationsRef(placeId), (snapshot) => {
    stationsCacheByPlace[placeId] = snapshot.docs.map((d) => docToStation(d))
    dispatchMenuUpdated()
  })
}

export function getStations(): Station[] {
  const placeId = getCurrentPlaceId()
  return stationsCacheByPlace[placeId] ?? []
}

export function saveStations(stations: Station[]): void {
  const placeId = getCurrentPlaceId()
  stationsCacheByPlace[placeId] = stations
  dispatchMenuUpdated()
  ;(async () => {
    const snap = await getDocs(stationsRef(placeId))
    const ids = new Set(stations.map((s) => s.id))
    const ops: BatchWriteOp[] = []
    for (const d of snap.docs) {
      if (!ids.has(d.id)) ops.push({ type: 'delete', ref: d.ref })
    }
    for (const s of stations) {
      ops.push({
        type: 'set',
        ref: stationDoc(placeId, s.id),
        data: stripUndefined(s) as object,
      })
    }
    await commitWriteOps(ops)
  })().catch((e) => console.error('Firestore saveStations failed', e))
}

function generateStationId(): string {
  return `S-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

export function addStation(station: Omit<Station, 'id'>): Station {
  const placeId = getCurrentPlaceId()
  const id = generateStationId()
  const full: Station = { ...station, id }
  const cache = stationsCacheByPlace[placeId] ?? []
  stationsCacheByPlace[placeId] = [...cache, full]
  dispatchMenuUpdated()
  setDoc(stationDoc(placeId, id), stripUndefined(full)).catch((e) =>
    console.error('Firestore addStation failed', e),
  )
  return full
}

export function updateStation(id: string, patch: Partial<Station>): Station[] {
  const placeId = getCurrentPlaceId()
  const cache = stationsCacheByPlace[placeId] ?? []
  stationsCacheByPlace[placeId] = cache.map((s) => (s.id === id ? { ...s, ...patch } : s))
  const found = stationsCacheByPlace[placeId].find((s) => s.id === id)
  if (found) {
    setDoc(stationDoc(placeId, id), stripUndefined(found)).catch((e) =>
      console.error('Firestore updateStation failed', e),
    )
  }
  dispatchMenuUpdated()
  return stationsCacheByPlace[placeId]
}

export function deleteStation(id: string): Station[] {
  const placeId = getCurrentPlaceId()
  const cache = stationsCacheByPlace[placeId] ?? []
  stationsCacheByPlace[placeId] = cache.filter((s) => s.id !== id)
  deleteDoc(stationDoc(placeId, id)).catch((e) =>
    console.error('Firestore deleteStation failed', e),
  )
  dispatchMenuUpdated()
  return stationsCacheByPlace[placeId]
}

export function isStationsPrimed(): boolean {
  const placeId = getCurrentPlaceId()
  return placeId in stationsCacheByPlace
}

/** Clear in-memory cache and unsubscribe from real-time listener. */
export function clearStationsCache(): void {
  if (stationsUnsubscribe) {
    stationsUnsubscribe()
    stationsUnsubscribe = null
  }
  Object.keys(stationsCacheByPlace).forEach((k) => delete stationsCacheByPlace[k])
}
